import { friends } from './friends.svelte'
import { isMobileBuild } from './platform'
import { parseSharedRoomTarget } from './room-target'
import { navigate, viewerPath } from './router.svelte'
import { defaultSignalUrl } from './signaling'
import type { RoomTarget } from './room-target'
import type { Friend } from './social-protocol'

export function roomHost(people: Friend[], target: RoomTarget): string | null {
  if (target.hostUserId) return target.hostUserId
  const matches = people.filter((person) => {
    if (person.presence.state !== 'sharing') return false
    try {
      const room = parseSharedRoomTarget(person.presence, defaultSignalUrl(), !isMobileBuild)
      return room.code === target.code && room.signalUrl === target.signalUrl
    } catch {
      return false
    }
  })
  return matches.length === 1 ? matches[0].user_id : null
}

export function replacementRoom(
  people: Friend[],
  host: string,
  current: RoomTarget,
): RoomTarget | null {
  const presence = people.find((person) => person.user_id === host)?.presence
  // A code alone cannot locate a replacement tunnel. Keep the known full link.
  if (presence?.state !== 'sharing' || !presence.share_url) return null
  try {
    const room = parseSharedRoomTarget(presence, defaultSignalUrl(), !isMobileBuild)
    if (room.code === current.code && room.signalUrl === current.signalUrl) return null
    return { ...room, hostUserId: host }
  } catch {
    return null
  }
}

// A single in-memory handoff survives the keyed viewer remount. Passwords
// stay out of URLs and persistent storage, and are consumed only by this room.
let recovery: { target: RoomTarget; name: string; password: string } | null = null
export function recoverSharedRoom(target: RoomTarget, name: string, password: string): void {
  recovery = { target, name, password }
  navigate(viewerPath(target.code, target), { replace: true })
}
export function takeRoomRecovery(target: RoomTarget): { name: string; password: string } | null {
  const pending = recovery
  recovery = null
  return pending &&
    pending.target.code === target.code &&
    pending.target.signalUrl === target.signalUrl &&
    pending.target.hostUserId === target.hostUserId
    ? { name: pending.name, password: pending.password }
    : null
}

/** All friend Watch buttons and invitations use the same validated room target. */
export function watchSharedRoom(
  room: { code: string; share_url?: string | null; from?: string },
  hostUserId?: string,
): void {
  try {
    let target = parseSharedRoomTarget(room, defaultSignalUrl(), !isMobileBuild)
    target.hostUserId = hostUserId ?? room.from
    if (target.hostUserId) {
      target = replacementRoom(friends.friends, target.hostUserId, target) ?? target
    }
    friends.notice = null
    navigate(viewerPath(target.code, target))
  } catch (error) {
    friends.notice = error instanceof Error ? error.message : String(error)
  }
}
